'use client'
import { useRouter } from 'next/navigation'
import { useLocale, useSetLocale } from '@/lib/i18n/hooks'
import { LOCALES, LOCALE_LABELS, LOCALE_FLAGS, type Locale } from '@/lib/i18n/config'

interface LocaleSwitcherProps {
  variant?: 'pill' | 'toggle'
  className?: string
}

export function LocaleSwitcher({ variant = 'pill', className }: LocaleSwitcherProps) {
  const locale = useLocale()
  const setLocale = useSetLocale()
  const router = useRouter()

  // El locale se persiste en la cookie `redy-locale` (client-side) pero el contenido
  // del microsite se renderiza en el servidor vía next-intl, que solo lee la cookie en
  // el render del servidor. Sin router.refresh() el server nunca re-lee la cookie y los
  // textos traducidos no cambian. refresh() preserva el estado de cliente y solo
  // refetchea los Server Components.
  const changeLocale = (l: Locale) => {
    setLocale(l)
    router.refresh()
  }

  if (variant === 'toggle') {
    const next = locale === 'es' ? 'en' : 'es'
    return (
      <button
        onClick={() => changeLocale(next)}
        className={className}
        aria-label={`Switch to ${LOCALE_LABELS[next]}`}
      >
        {LOCALE_FLAGS[locale]} {locale.toUpperCase()}
      </button>
    )
  }

  return (
    <div className={`flex gap-1 ${className ?? ''}`}>
      {LOCALES.map((l) => (
        <button
          key={l}
          onClick={() => changeLocale(l)}
          className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
            locale === l
              ? 'bg-primary text-white'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          {LOCALE_FLAGS[l]} {l.toUpperCase()}
        </button>
      ))}
    </div>
  )
}
